/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import ComponentRegistry from '../components-registry';
import Constants from '../constants';
import { C11nEnv } from '../interpreter/c11n-env';
import type { NodeProps } from '../contextree/types';
import Utils from '../utils/utils';

import AnnotationsHandler from '.';

type Props = { [key: string]: object[] };

type Config = {
  type: string;
  config: { [key: string]: string };
};

class AnnotationProcessor {
  static handleAnnotation(anno: string, c11nEnv: C11nEnv, contextTreeNodeProps?: NodeProps) {
    // example Anno format @L Localize Me
    const spaceIndex = anno.indexOf(' ');
    const annotationName = spaceIndex === -1 ? anno : anno.substring(0, spaceIndex);
    const handler = AnnotationsHandler.getHandler(annotationName);
    if (!handler) {
      return anno;
    }
    return handler(anno.substring(spaceIndex + 1), c11nEnv, contextTreeNodeProps);
  }

  static trimAnnotation<T extends string | number>(anno: T) {
    /** if annotation is string is for list index */
    if (typeof anno !== 'string') {
      return anno;
    }
    const spaceIndex = anno.indexOf(' ');
    const format = anno.substring(0, spaceIndex);
    const value = anno.substring(spaceIndex + 1);
    const handler = AnnotationsHandler.getHandler(format);
    if (!handler) {
      return anno;
    }
    return value;
  }

  static handleComponentMapping(props: Props, config: Config, c11nEnv: C11nEnv) {
    const component = ComponentRegistry.getComponent(config.type);
    if (!component?.mapping) {
      return;
    }
    const annoMap = component.mapping(config.config);
    if (annoMap && annoMap['@map']) {
      // Handle map attribute
      const mapObject = annoMap['@map'];

      const { source } = mapObject;
      if (!source) {
        return;
      }
      const sourceRecords = c11nEnv.getValue(source);

      if (typeof mapObject.advanced === 'function') {
        mapObject.advanced(props, sourceRecords);
      } else {
        const mapInputs = mapObject.inputs_map;

        // map output property to context object
        props[mapObject.output_property] = [];
        const propertyOutput = props[mapObject.output_property];

        sourceRecords.forEach((record: Props) => {
          // Process the map_inputs
          const mapOutput: { [key: string]: object | string } = {};
          Object.keys(mapInputs).forEach((key) => {
            let val = mapInputs[key];
            if (val && val.indexOf('.') === 0) {
              val = val.substring(1);
              mapOutput[key] = record[val];
            } else {
              mapOutput[key] = val;
            }
          });

          // Update the property output
          propertyOutput.push(mapOutput);
        });
      }
    }
  }
  /**
   * This returns the literal property name
   * @param value propertyName with annotation
   * @example
   * if we pass '@P .pyName' it will return pyName
   */

  static getPropertyName(value: string = '') {
    if (value && Utils.isString(value)) {
      const isProperty =
        value.startsWith(Constants.PROPERTY_ANNOTATION) ||
        value.startsWith(Constants.ATTACHMENT_ANNOTATION) ||
        value.startsWith(Constants.ASSOCIATED_PROPERTY_ANNOTATION);
      const index = isProperty ? value.indexOf(' ') : -1;
      return isProperty ? value.substring(index + 2) : value;
    }
    return value;
  }

  /**
   * This returns the leaf property name of an embedded property
   * @param value propertyname with annotation
   * @example
   * if we pass '@P .Expenses.Home' it will return 'Home'
   */

  static getLeafPropertyName(value: string) {
    const isProperty = Utils.isString(value) && !!value?.startsWith(Constants.PROPERTY_ANNOTATION);

    if (isProperty) {
      const propertyName = this.getPropertyName(value);

      const splitPropertyName = propertyName.split('.');
      return splitPropertyName.length < 2 ? propertyName : splitPropertyName.pop();
    }

    return value;
  }

  /**
   * This will check whether the passed value is property if yes return true else false
   * @param value propertyName with annotation
   * @example
   * returns true if value '@P .pyName' else return false
   */

  static isProperty(value: string) {
    return Utils.isString(value) && !!value?.startsWith(Constants.PROPERTY_ANNOTATION);
  }
}

export default AnnotationProcessor;
